package nl.volleyplanner.web.controller.thuisprogramma.assignee_finder

import kotlinx.html.*
import kotlinx.html.js.onChangeFunction
import nl.volleyplanner.api.planner.PlannerTaskAssignmentConstraint
import nl.volleyplanner.api.planner.TaskAssignmentAdvice
import nl.volleyplanner.api.sports.Match
import nl.volleyplanner.sportconfig.NevoboAgeGroup
import nl.volleyplanner.types.AgeGroup
import nl.volleyplanner.web.base.HTMLComponent
import nl.volleyplanner.web.base.component.*
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement

class AssigneeFinderFilters(val match: Match, val filterManager: FilterManager<TaskAssignmentAdvice>) {
    val HasMatchFilter = object : Filter<TaskAssignmentAdvice> {
        override fun showRecord(record: TaskAssignmentAdvice): Boolean {
            return record.personMatchesOnTaskDate.isNotEmpty()
        }
    }
    val HasLicenseFilter = object : Filter<TaskAssignmentAdvice> {
        override fun showRecord(record: TaskAssignmentAdvice): Boolean {
            return !record.newScoreImpactList.any { it.constraint.constraintName == PlannerTaskAssignmentConstraint.NotEligibleForThisTask }
        }
    }
    val HasNoOtherTasksFilter = object : Filter<TaskAssignmentAdvice> {
        val constraintFilters = listOf(PlannerTaskAssignmentConstraint.OneTaskPerWeek)
        override fun showRecord(record: TaskAssignmentAdvice): Boolean {
            return record.newScoreImpactList.none { constraintFilters.contains(it.constraint.constraintName) }
        }
    }
    val OnlyWithoutTooManyTasksFilter = object : Filter<TaskAssignmentAdvice> {
        override fun showRecord(record: TaskAssignmentAdvice): Boolean {
            return record.newScoreImpactList.none { it.constraint.constraintName == PlannerTaskAssignmentConstraint.EvenlySpreadOverPersons && (it.impact.softScore <= -1 || it.impact.hardScore < 0) }
        }
    }
    val OnlyWithoutOverlappingMatchFilter = object : Filter<TaskAssignmentAdvice> {
        override fun showRecord(record: TaskAssignmentAdvice): Boolean {
            return !record.newScoreImpactList.any {
                it.constraint.constraintName == PlannerTaskAssignmentConstraint.NoTaskDuringOwnMatch ||
                it.constraint.constraintName == PlannerTaskAssignmentConstraint.NoTaskForOwnMatch
            }
        }
    }
    val HasShortDurationToOwnMatchFilter = object : Filter<TaskAssignmentAdvice> {
        override fun showRecord(record: TaskAssignmentAdvice): Boolean {
            return record.personMatchesOnTaskDate.isNotEmpty() && record.personMatchesOnTaskDate.any { (it.durationTo(match)?.inWholeMinutes ?: 999) < 240 }
        }
    }

    class AgeGroupFilter(private val ageGroup: AgeGroup) : Filter<TaskAssignmentAdvice> {
        override fun showRecord(record: TaskAssignmentAdvice): Boolean {
            return record.person.ageGroup == ageGroup
        }
    }

    val IsSeniorFilter = AgeGroupFilter(NevoboAgeGroup.Senior)
    val IsAYouthFilter = AgeGroupFilter(NevoboAgeGroup.A)
    val IsBYouthFilter = AgeGroupFilter(NevoboAgeGroup.B)
    val IsCYouthFilter = AgeGroupFilter(NevoboAgeGroup.C)
    val IsCMVYouthFilter = AgeGroupFilter(NevoboAgeGroup.CMV)

    val SeniorGroupCompositeFilter = CompositeFilter(filterManager, CompositeFilterType.Or)
        .apply { setFilters(listOf(IsSeniorFilter, IsAYouthFilter, IsBYouthFilter, IsCYouthFilter)) }

    init {
        filterManager.setFilters(
            listOf(
                HasMatchFilter, HasLicenseFilter, OnlyWithoutOverlappingMatchFilter,
                HasNoOtherTasksFilter,
                SeniorGroupCompositeFilter
            )
        )
        filterManager.launchManager()
    }
}

/**
 * Component displaying all filters for assignee candidates to show/hide
 */
class AssigneeFilterComponent(
    val filters: AssigneeFinderFilters
) : HTMLComponent() {
    val filterManager = filters.filterManager

    private fun DIV.filterToggle2(
        filterName: String,
        filterLabel: String,
        filterCollection: FilterCollection<TaskAssignmentAdvice>,
        filter: Filter<TaskAssignmentAdvice>,
        inverseFilter: Boolean = false
    ) {
        div(classes = "form-check form-switch") {
            input(classes = "form-check-input") {
                type = InputType.checkBox
                role = "switch"
                id = "filter-${filterName.lowercase()}"
                checked =
                    if (!inverseFilter) filterCollection.hasFilter(filter) else !filterCollection.hasFilter(filter)

                onChangeFunction = { event ->
                    val state = (event.target as HTMLInputElement).checked
                    if (!inverseFilter) {
                        if (state) filterCollection.addFilter(filter)
                        else filterCollection.removeFilter(filter)
                    } else {
                        if (!state) filterCollection.addFilter(filter)
                        else filterCollection.removeFilter(filter)
                    }
                }
            }
            label(classes = "form-check-label") {
                htmlFor = "filter-${filterName.lowercase()}"
                +filterLabel
            }
        }
    }

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        return consumer.div(classes = "row p-2 mb-2") {
            h4 { +"Filters" }
            div(classes = "col-8") {
                filterToggle2("hasMatchFilter", "Heeft thuiswedstrijd", filterManager, filters.HasMatchFilter)
                filterToggle2(
                    "hasNoOverlappingMatchFilter",
                    "Geen overlap met eigen wedstrijd",
                    filterManager,
                    filters.OnlyWithoutOverlappingMatchFilter
                )
                // Need to think if we need this if the list is ordered anyway
//                filterToggle2(
//                    "hasShortDurationToOwnMatchFilter",
//                    "Eigen wedstrijd binnen 4u",
//                    filterManager,
//                    filters.HasShortDurationToOwnMatchFilter
//                )
                filterToggle2("hasLicenseFilter", "Heeft licentie(s)", filterManager, filters.HasLicenseFilter)
                // Need to redo filter
//                filterToggle2(
//                    "OnlyWithoutTooManyTasksFilter",
//                    "Zit niet op max taken",
//                    filterManager,
//                    filters.OnlyWithoutTooManyTasksFilter
//                )
                filterToggle2(
                    "HasNoOtherTasksFilter",
                    "Nog geen taken vandaag",
                    filterManager,
                    filters.HasNoOtherTasksFilter
                )
            }
            div(classes = "col-4") {
                filterToggle2("isSeniorFilter", "Senioren", filters.SeniorGroupCompositeFilter, filters.IsSeniorFilter)
                filterToggle2("isAYouthFilter", "A Jeugd", filters.SeniorGroupCompositeFilter, filters.IsAYouthFilter)
                filterToggle2("isBYouthFilter", "B Jeugd", filters.SeniorGroupCompositeFilter, filters.IsBYouthFilter)
                filterToggle2("isCYouthFilter", "C Jeugd", filters.SeniorGroupCompositeFilter, filters.IsCYouthFilter)
                filterToggle2("isCYouthFilter", "CMV Jeugd", filters.SeniorGroupCompositeFilter, filters.IsCMVYouthFilter)
            }
        }
    }

    override suspend fun getState() = null
}